import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DoseInputError, validatePhysicalGy } from '~/domain/dose/doseFactor';
import { doseGridOverImage } from '~/domain/dose/doseGrid';
import { buildDerivedDose } from '~/domain/dose/derivedDose';
import { mapDoseGy } from '~/domain/dose/mapDose';
import { scaledDoseGy } from '~/domain/dose/scaleDose';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { StructureSet } from '~/domain/patient/structureset';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    DoseMappingStatus,
    MappedDoseRecord,
    replanningDoseMappingState,
    replanningDoseScaleState,
    replanningMappedDosesState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { generateUID } from '~/utils/dicomUid';
import { ReviewTransformOption, rigidReviewMapping } from './reviewTransforms';
import { useReviewDatasets } from './useReviewDatasets';
import { logErrorName } from '~/utils/logErrorName';

export const TARGET_DOSE_VERSION = 'Workspace+ review dose 2';

export interface ReviewTargetDose {
    // The dose drawn on the target: the run's own, or one computed from it or from the prior dose.
    readonly dose?: DoseMatrix;
    // The structures it is drawn with.
    readonly structureSet?: StructureSet;
    readonly record?: MappedDoseRecord;
    readonly status?: DoseMappingStatus;
    readonly blockedReasonId?: string;
    // The transform whose result the target shows; it lags the selection while that result is not ready.
    readonly drawnKind?: ReviewTransformOption['kind'];
    readonly retry: () => void;
}

// Deformable: the dose the run returned, scaled when asked. Rigid: the prior dose moved by the rigid, scaled.
// Every scale is applied to the original dose, and the result is drawn on the target scan.
export function useReviewTargetDose(
    transform: ReviewTransformOption | undefined,
    rigidStructures: StructureSet | undefined
): ReviewTargetDose {
    const intl = useIntl();
    const patient = useSelectedPatient();
    const session = useSessionOrNull();
    const { priorDose, propagatedSet, runDose } = useReviewDatasets();
    const scale = useRecoilValue(replanningDoseScaleState);
    const [records, setRecords] = useRecoilState(replanningMappedDosesState);
    const [status, setStatus] = useRecoilState(replanningDoseMappingState);
    const { setEnvironmentDose, setEnvironmentStructureSet } =
        useViewportEnvironment();
    const { deleteDose } = useDeleteDataset();
    const [attempt, setAttempt] = useState(0);
    const [drawnKind, setDrawnKind] = useState<ReviewTransformOption['kind']>();
    const primarySeriesUid = useSelectedPrimaryImage()?.seriesUid;
    const currentDose = useCurrentDose();

    const kind = transform?.kind;
    const targetImage = propagatedSet?.image ?? undefined;
    const planningImage = priorDose?.structureSet?.image ?? undefined;
    const structureSet = kind === 'rigid' ? rigidStructures : propagatedSet;
    const source = kind === 'rigid' ? priorDose : runDose;

    const blockedReasonId = !transform
        ? 'replanning.dose.blocked.noTransform'
        : !propagatedSet || !targetImage
          ? 'replanning.dose.blocked.noTarget'
          : kind === 'rigid' && !priorDose
            ? 'replanning.dose.blocked.noPriorDose'
            : kind === 'deformable' && !runDose
              ? 'replanning.dose.blocked.noRunDose'
              : undefined;

    // The run's own dose needs nothing computed; anything else is keyed by what it is computed from.
    const usesRunDose = kind === 'deformable' && scale === 1;
    const key =
        !blockedReasonId && !usesRunDose && source && structureSet && transform
            ? [
                  kind,
                  transform.sopInstanceUid,
                  source.id,
                  structureSet.id,
                  scale,
              ].join('|')
            : undefined;

    const record = records.find((candidate) => candidate.key === key);
    const computed = record
        ? patient?.doses.find((dose) => dose.id === record.doseId)
        : undefined;
    const dose = usesRunDose && !blockedReasonId ? runDose : computed;

    const held = useRef({
        patient,
        session,
        records,
        setEnvironmentDose,
        setEnvironmentStructureSet,
        deleteDose,
        intl,
    });
    useLayoutEffect(() => {
        held.current = {
            patient,
            session,
            records,
            setEnvironmentDose,
            setEnvironmentStructureSet,
            deleteDose,
            intl,
        };
    });
    const latestKey = useRef<string>();
    const shownKey = useRef<string>();
    const statusRef = useRef(status);
    statusRef.current = status;

    useEffect(() => {
        latestKey.current = key;
        if (
            !key ||
            !kind ||
            !transform ||
            !source ||
            !structureSet ||
            !targetImage
        ) {
            return;
        }
        const existing = held.current.records.find(
            (candidate) => candidate.key === key
        );
        if (
            existing &&
            held.current.patient?.doses.some(
                (candidate) => candidate.id === existing.doseId
            )
        ) {
            return;
        }
        // A failure stays until the user retries; a run in flight is not restarted.
        if (statusRef.current?.key === key) {
            return;
        }
        setStatus({ status: 'mapping', key });
        void (async () => {
            try {
                validatePhysicalGy(source);
                let grid = source.grid;
                let doseGy: Float32Array;
                let unmappedVoxels = 0;
                let outsideSourceVoxels = 0;
                if (kind === 'rigid') {
                    if (!planningImage) {
                        throw new DoseInputError(
                            'The prior dose has no planning scan.',
                            'replanning.dose.blocked.noPriorDose'
                        );
                    }
                    const mapping = rigidReviewMapping(
                        transform.registration,
                        targetImage.frameOfReferenceUid,
                        planningImage.frameOfReferenceUid
                    );
                    grid =
                        runDose?.grid ??
                        doseGridOverImage(targetImage.grid, [
                            source.grid.resX,
                            source.grid.resY,
                            source.grid.resZ,
                        ]);
                    const result = await mapDoseGy(
                        source.realPixels,
                        source.grid,
                        grid,
                        mapping,
                        scale,
                        { isCancelled: () => latestKey.current !== key }
                    );
                    if (!result) {
                        return;
                    }
                    doseGy = result.doseGy;
                    unmappedVoxels = result.unmappedVoxels;
                    outsideSourceVoxels = result.outsideSourceVoxels;
                } else {
                    doseGy = scaledDoseGy(source.realPixels, scale);
                }
                if (latestKey.current !== key) {
                    return;
                }
                const current = held.current;
                if (!current.session) {
                    throw new Error('No patient session');
                }
                const scaleText = current.intl.formatNumber(scale, {
                    maximumFractionDigits: 3,
                });
                const description = current.intl.formatMessage(
                    {
                        id:
                            kind === 'rigid'
                                ? 'replanning.dose.target.rigidLabel'
                                : 'replanning.dose.target.scaledLabel',
                    },
                    { scale: scaleText }
                );
                const comment =
                    kind === 'rigid'
                        ? `Prior dose resampled onto the target scan through the rigid registration, ` +
                          `not recalculated. Scale ${scale} applied to the prior dose. Trilinear at voxel ` +
                          `centers; 0 Gy outside the prior dose grid (${outsideSourceVoxels} voxels) and ` +
                          `outside the registration (${unmappedVoxels} voxels). Grid ` +
                          `${grid.sizeX}x${grid.sizeY}x${grid.sizeZ} at ${grid.resX}x${grid.resY}x${grid.resZ} mm. ` +
                          `${TARGET_DOSE_VERSION}.`
                        : `Dose returned by the run, scaled by ${scale}; not recalculated. ${TARGET_DOSE_VERSION}.`;
                const built = buildDerivedDose({
                    sopInstanceUid: generateUID(),
                    frameOfReferenceUid: targetImage.frameOfReferenceUid,
                    grid,
                    doseGy,
                    description,
                    comment,
                    summationType: source.doseSummationType,
                    softwareVersion: TARGET_DOSE_VERSION,
                    referencedStructureSetUid: structureSet.dicomUid,
                });
                current.session.doseService.add(built, structureSet);
                const superseded = current.records.filter(
                    (candidate) =>
                        candidate.transformUid === transform.sopInstanceUid &&
                        candidate.key !== key
                );
                setRecords((all) => [
                    ...all.filter(
                        (candidate) =>
                            candidate.transformUid !== transform.sopInstanceUid
                    ),
                    {
                        key,
                        transformUid: transform.sopInstanceUid,
                        doseId: built.id,
                        totalVoxels: doseGy.length,
                        unmappedVoxels,
                        outsideSourceVoxels,
                    },
                ]);
                setStatus((now) => (now?.key === key ? undefined : now));
                // One computed dose per transform: a new scale replaces the old one.
                for (const old of superseded) {
                    const oldDose = current.patient?.doses.find(
                        (candidate) => candidate.id === old.doseId
                    );
                    if (oldDose) {
                        current.deleteDose(oldDose);
                    }
                }
            } catch (error) {
                logErrorName('Target dose could not be computed:', error);
                setStatus((now) =>
                    now?.key === key
                        ? {
                              status: 'failed',
                              key,
                              messageId:
                                  error instanceof DoseInputError
                                      ? error.messageId
                                      : 'replanning.dose.error.mapFailed',
                          }
                        : now
                );
            }
        })();
    }, [
        key,
        // Re-runs a failed key on retry.
        attempt,
        kind,
        transform,
        source,
        structureSet,
        targetImage,
        planningImage,
        runDose,
        scale,
        setRecords,
        setStatus,
    ]);

    // Drawn once per change, and only over the target scan: drawn over another scan it would replace that scan.
    const targetSeriesUid = targetImage?.seriesUid;
    const shownFor = dose?.id ?? structureSet?.id;
    // Redraw if a later structure-set selection unloaded the dose.
    const drawnDoseGone = !!dose && !currentDose;
    useEffect(() => {
        if (
            !shownFor ||
            (shownKey.current === String(shownFor) && !drawnDoseGone) ||
            !targetSeriesUid ||
            primarySeriesUid !== targetSeriesUid ||
            // The rigid set is drawn alone while its dose maps; the deformable set waits for its dose.
            (kind !== 'rigid' &&
                !dose &&
                status?.key === key &&
                status?.status === 'mapping')
        ) {
            return;
        }
        const current = held.current;
        const patientId = current.patient?.dataSet.PatientID ?? '';
        shownKey.current = String(shownFor);
        if (dose) {
            current.setEnvironmentDose([dose], patientId);
            setDrawnKind(kind);
        } else if (structureSet && (!source || kind === 'rigid')) {
            current.setEnvironmentStructureSet([structureSet], patientId);
            setDrawnKind(kind);
        }
    }, [
        kind,
        shownFor,
        dose,
        structureSet,
        source,
        primarySeriesUid,
        targetSeriesUid,
        status,
        key,
        drawnDoseGone,
    ]);

    const retry = useCallback(() => {
        setStatus((now) => (now?.status === 'failed' ? undefined : now));
        setAttempt((n) => n + 1);
    }, [setStatus]);

    return {
        dose,
        structureSet,
        drawnKind,
        record: computed ? record : undefined,
        status: status?.key === key ? status : undefined,
        blockedReasonId,
        retry,
    };
}
